/**
 * «Análisis» (the owner, 2026-10-06: «a new analytics page that has a bunch of useful analysis
 * tools»; asked what should go on it, they took all four of: sales, products, clients, costs).
 *
 * «Contabilidad» says the shop as statements and «Control» as who did what; this says it as the
 * questions an owner asks. How are we doing, and when do we sell? What makes money, and what is
 * sitting on the shelf? Who owes, and who stopped coming? Where does the money go? Each answer
 * comes from what the phone already holds, and the figures add up the way «Contabilidad»'s profit
 * by product and by client do (`netoDeVenta`): returns and discounts off, delivery and cash
 * rounding aside. Pure, so the screen and its tests share one answer.
 */

import {
  cordobasToUsd,
  costOf,
  diasEntre,
  formatMoney,
  sumarDias,
  ZONA_HORARIA,
  type Centavos,
  type FxRate,
  type Milli,
} from '@abastece/shared';
import {
  netoDeVenta,
  type Abono,
  type Cliente,
  type Compra,
  type Devolucion,
  type Existencia,
  type FilaUtilidad,
  type Incobrable,
  type Producto,
  type Venta,
} from '@abastece/core';
import { estadoDeCuenta, pendientesDelFiado } from './fiado.js';
import { otroMes } from './historial.js';
import type { MonedaDeLosLibros } from './libros.js';

// ---------------------------------------------------------------------------
// Los períodos
// ---------------------------------------------------------------------------

/**
 * The stretches an owner compares: few, so they sit in a row or two with nothing to scroll. And
 * `rango`, two days picked on the calendar (the owner, 2026-10-13), which are not in the row.
 */
export type PeriodoAnalisis = 'hoy' | 'ayer' | 'mes' | 'mesPasado' | 'tresMeses' | 'anio' | 'rango';
/** «Hoy» and «Ayer» first, as the histories have them (the owner, 2026-10-14). */
export const PERIODOS_ANALISIS: readonly Exclude<PeriodoAnalisis, 'rango'>[] = ['hoy', 'ayer', 'mes', 'mesPasado', 'tresMeses', 'anio'];

/** What it answers, a tab each; «Hoy», what to do about it, first. */
export type VistaAnalisis = 'hoy' | 'ventas' | 'productos' | 'clientes' | 'compras' | 'gastos' | 'dinero';
export const VISTAS_ANALISIS: readonly VistaAnalisis[] = ['hoy', 'ventas', 'productos', 'clientes', 'compras', 'gastos', 'dinero'];

/** What the address keeps of the screen. */
export interface BusquedaAnalisis {
  vista?: VistaAnalisis;
  periodo?: PeriodoAnalisis;
  /** The days picked, with `periodo` «rango» only. */
  desde?: string;
  hasta?: string;
}

const esDia = (x: unknown): x is string => typeof x === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(x) && !Number.isNaN(Date.parse(`${x}T00:00:00Z`));

/**
 * What the address keeps of the screen: the tab and the period, the first of each left out, and the
 * two days of a period picked on the calendar. Kept there so the phone's back button, from a client
 * or a product opened here, comes back to the tab and the period it left rather than to the first
 * of each. Days that are not days, or the last before the first, are no period: this month.
 */
export function busquedaDeAnalisis(s: Record<string, unknown>): BusquedaAnalisis {
  const vista = VISTAS_ANALISIS.find((v) => v === s['vista']);
  const nombrado = PERIODOS_ANALISIS.find((p) => p === s['periodo']);
  const rango =
    s['periodo'] === 'rango' && esDia(s['desde']) && esDia(s['hasta']) && s['desde'] <= s['hasta']
      ? { periodo: 'rango' as const, desde: s['desde'], hasta: s['hasta'] }
      : null;
  return {
    ...(vista && vista !== 'hoy' ? { vista } : {}),
    ...(rango ?? (nombrado && nombrado !== 'mes' ? { periodo: nombrado } : {})),
  };
}

/** Two days, both counted in. */
export interface Tramo {
  desde: string;
  hasta: string;
}

/** How a chart over the period is cut: by day for a month, by week for three, by month for a year. */
export type Paso = 'dia' | 'semana' | 'mes';

/** The period and the one it is compared with, and how its chart is cut. */
export interface Tramos {
  actual: Tramo;
  anterior: Tramo;
  paso: Paso;
}

const finDeMes = (mes: string): string => sumarDias(`${otroMes(mes, 1)}-01`, -1);
const dosCifras = (n: number): string => String(n).padStart(2, '0');

/**
 * The period and the one it is compared with, as of `hoyEs`. Today against yesterday, and yesterday
 * against the day before, as one day picked on the calendar is; this month so far against the same
 * days of last month; last month against the one before; the last thirteen weeks against the
 * thirteen before them; this year so far against the same days of last year. Days picked on the
 * calendar (`rango`), against as many days right before them, no further than today: cut by day up
 * to a month and a half, by week up to five months, by month past that. Without days, this month.
 */
export function tramosDe(p: PeriodoAnalisis, hoyEs: string, elegido?: Tramo): Tramos {
  const mes = hoyEs.slice(0, 7);
  if (p === 'rango' && !elegido) return tramosDe('mes', hoyEs);
  switch (p) {
    case 'hoy':
    case 'ayer': {
      const dia = p === 'hoy' ? hoyEs : sumarDias(hoyEs, -1);
      const antes = sumarDias(dia, -1);
      return { actual: { desde: dia, hasta: dia }, anterior: { desde: antes, hasta: antes }, paso: 'dia' };
    }
    case 'rango': {
      const hasta = elegido!.hasta < hoyEs ? elegido!.hasta : hoyEs;
      const desde = elegido!.desde < hasta ? elegido!.desde : hasta;
      const dias = diasEntre(desde, hasta) + 1;
      return {
        actual: { desde, hasta },
        anterior: { desde: sumarDias(desde, -dias), hasta: sumarDias(desde, -1) },
        paso: dias <= 45 ? 'dia' : dias <= 150 ? 'semana' : 'mes',
      };
    }
    case 'mes': {
      const antes = otroMes(mes, -1);
      const dia = Math.min(Number(hoyEs.slice(8)), Number(finDeMes(antes).slice(8)));
      return { actual: { desde: `${mes}-01`, hasta: hoyEs }, anterior: { desde: `${antes}-01`, hasta: `${antes}-${dosCifras(dia)}` }, paso: 'dia' };
    }
    case 'mesPasado': {
      const uno = otroMes(mes, -1);
      const dos = otroMes(mes, -2);
      return { actual: { desde: `${uno}-01`, hasta: finDeMes(uno) }, anterior: { desde: `${dos}-01`, hasta: finDeMes(dos) }, paso: 'dia' };
    }
    case 'tresMeses': {
      const desde = sumarDias(hoyEs, -90);
      return { actual: { desde, hasta: hoyEs }, anterior: { desde: sumarDias(desde, -91), hasta: sumarDias(desde, -1) }, paso: 'semana' };
    }
    case 'anio': {
      const anio = Number(hoyEs.slice(0, 4));
      const mismoDia = `${anio - 1}-${hoyEs.slice(5)}`;
      // The 29th of February had no day the year before.
      const hasta = mismoDia.endsWith('-02-29') ? `${anio - 1}-02-28` : mismoDia;
      return { actual: { desde: `${anio}-01-01`, hasta: hoyEs }, anterior: { desde: `${anio - 1}-01-01`, hasta }, paso: 'mes' };
    }
  }
}

/** How many days a stretch has, both ends counted. */
export const diasDe = (t: Tramo): number => diasEntre(t.desde, t.hasta) + 1;

/** Today alone, which is not over: what it is compared with was a whole day. */
export const esSoloHoy = (t: Tramo, hoyEs: string): boolean => t.desde === hoyEs && t.hasta === hoyEs;

const dentro = (fecha: string, t: Tramo): boolean => fecha >= t.desde && fecha <= t.hasta;
const vigentesEn = (ventas: readonly Venta[], t: Tramo): Venta[] => ventas.filter((v) => !v.anulada && dentro(v.fecha, t));

/**
 * How much a figure moved against the period before, in basis points. Null with nothing before to
 * compare, and for a loss: a profit of C$ 19 become a loss of C$ 5,041 did not move «26,550%».
 */
export const cambioBp = (ahora: number, antes: number): number | null =>
  antes > 0 && ahora >= 0 ? Math.round(((ahora - antes) * 10_000) / antes) : null;

const margen = (utilidad: number, ingreso: number): number | null => (ingreso > 0 ? Math.round((utilidad * 10_000) / ingreso) : null);

// ---------------------------------------------------------------------------
// El dinero
// ---------------------------------------------------------------------------

/** Holds a currency's sign to its figure, so a sentence never ends a line on «C$». */
const PEGADO = ' ';

/** How «Análisis» writes money: a figure, and what one unit cost. */
export type DineroDeAnalisis = ((c: number) => string) & {
  /** What a supplier charged for one unit (`AlzaDeCosto`), centavos and all. */
  porUnidad: (c: number) => string;
};

/**
 * How «Análisis» writes money. Its figures are for seeing how things go, not for squaring a till: to
 * the córdoba. In dollars (the owner, 2026-10-13: «the analytics page also needs a cordoba/dollar
 * toggle similar to the accounting page») at the rate the books are kept at, as «Contabilidad» reads
 * them (`dineroDeLosLibros`). A dollar is some thirty-six córdobas, so to the dollar a sale's average
 * of C$ 150 would read «US$ 4» and a product that left C$ 15, «US$ 0»: dollars go to the cent under a
 * hundred, and to the dollar from there up, where a cent says no more than a córdoba does.
 *
 * What one unit cost keeps its centavos; in dollars a unit of a few córdobas is a few cents, and a
 * fifth more on one of C$ 1 would read «US$ 0.03 → US$ 0.03»: under a dollar it keeps a third
 * decimal, «US$ 0.027 → US$ 0.033».
 *
 * «Por comprar» writes its estimates and its last costs the same way (2026-10-16): they too are for
 * seeing, not for paying.
 */
export function dineroDeAnalisis(moneda: MonedaDeLosLibros, tasa: FxRate): DineroDeAnalisis {
  if (moneda === 'NIO') {
    return Object.assign(
      // Half a córdoba away from zero either way: Math.round alone turns -C$ 30,100.50 into -C$ 30,100
      // beside C$ 30,101 for the same amount coming in.
      (c: number) => formatMoney((Math.sign(c) * Math.round(Math.abs(c) / 100) * 100) as Centavos).replace(/\.00$/, '').replace('C$ ', `C$${PEGADO}`),
      { porUnidad: (c: number) => formatMoney(Math.round(c) as Centavos).replace('C$ ', `C$${PEGADO}`) },
    );
  }
  const enDolares = (c: number): number => cordobasToUsd(Math.round(c) as Centavos, tasa);
  const signo = (n: number): string => (n < 0 ? '-' : '');
  return Object.assign(
    (c: number) => {
      const usd = enDolares(c);
      const cents = Math.abs(usd);
      const texto =
        cents >= 10_000
          ? formatMoney((Math.round(cents / 100) * 100) as Centavos, { symbol: false }).replace(/\.00$/, '')
          : formatMoney(cents as Centavos, { symbol: false });
      return `${signo(usd)}US$${PEGADO}${texto}`;
    },
    {
      porUnidad: (c: number) => {
        const usd = enDolares(c);
        if (Math.abs(usd) >= 100) return `${signo(usd)}US$${PEGADO}${formatMoney(Math.abs(usd) as Centavos, { symbol: false })}`;
        // Tenths of a cent: ten times the centavos, turned as centavos are.
        const milesimas = cordobasToUsd(Math.round(c * 10) as Centavos, tasa);
        return `${signo(milesimas)}US$${PEGADO}${(Math.abs(milesimas) / 1_000).toFixed(3)}`;
      },
    },
  );
}

// ---------------------------------------------------------------------------
// Las ventas
// ---------------------------------------------------------------------------

export interface ResumenVentas {
  ventas: number;
  ingreso: Centavos;
  costo: Centavos;
  utilidad: Centavos;
  /** Gross margin over what was sold, in basis points; null when nothing was. */
  margenBp: number | null;
  /** What a sale came to on average; null with no sales. */
  ticket: Centavos | null;
}

/** The sales of a stretch: how many, what they brought in, what they cost and left, a ticket's average. */
export function resumenDeVentas(ventas: readonly Venta[], t: Tramo): ResumenVentas {
  let ingreso = 0;
  let costo = 0;
  let n = 0;
  for (const v of vigentesEn(ventas, t)) {
    const neto = netoDeVenta(v);
    ingreso += neto.ingreso;
    costo += neto.costo;
    n += 1;
  }
  const utilidad = ingreso - costo;
  return {
    ventas: n,
    ingreso: ingreso as Centavos,
    costo: costo as Centavos,
    utilidad: utilidad as Centavos,
    margenBp: margen(utilidad, ingreso),
    ticket: n > 0 ? (Math.round(ingreso / n) as Centavos) : null,
  };
}

export interface Punto extends Tramo {
  ingreso: Centavos;
  utilidad: Centavos;
  ventas: number;
}

/** The days, weeks or months a stretch is cut into: every one of them, those with no sales too. */
export function cortes(t: Tramo, paso: Paso): Tramo[] {
  const salida: Tramo[] = [];
  for (let d = t.desde; d <= t.hasta; ) {
    const fin = paso === 'dia' ? d : paso === 'semana' ? sumarDias(d, 6) : finDeMes(d.slice(0, 7));
    const hasta = fin < t.hasta ? fin : t.hasta;
    salida.push({ desde: d, hasta });
    d = sumarDias(hasta, 1);
  }
  return salida;
}

/** The day, week or month that sold the most; null when none sold anything. The first of a tie. */
export function mejorPunto(serie: readonly Punto[]): Punto | null {
  return serie.reduce<Punto | null>((mejor, p) => (p.ingreso > 0 && (!mejor || p.ingreso > mejor.ingreso) ? p : mejor), null);
}

/** What was sold and left in each day, week or month of a stretch. */
export function serieDeVentas(ventas: readonly Venta[], t: Tramo, paso: Paso): Punto[] {
  const puntos: Punto[] = cortes(t, paso).map((c) => ({ ...c, ingreso: 0 as Centavos, utilidad: 0 as Centavos, ventas: 0 }));
  const porDia = new Map<string, Punto>();
  for (const p of puntos) for (let d = p.desde; d <= p.hasta; d = sumarDias(d, 1)) porDia.set(d, p);
  for (const v of vigentesEn(ventas, t)) {
    const p = porDia.get(v.fecha);
    if (!p) continue;
    const neto = netoDeVenta(v);
    p.ingreso = (p.ingreso + neto.ingreso) as Centavos;
    p.utilidad = (p.utilidad + neto.ingreso - neto.costo) as Centavos;
    p.ventas += 1;
  }
  return puntos;
}

/** A way of paying's share of what came in, the change given taken off where it came out of. */
export interface ParteDePago {
  forma: string;
  monto: Centavos;
  /** Of everything that came in, in basis points. */
  parteBp: number;
}

/** What came in each way, largest first, from what each way brought in (`cobradoPorForma`). */
export function partesDePago(porForma: Readonly<Partial<Record<string, Centavos>>>): ParteDePago[] {
  const filas = Object.entries(porForma).filter((e): e is [string, Centavos] => (e[1] ?? 0) > 0);
  const total = filas.reduce((a, [, m]) => a + m, 0);
  return filas
    .map(([forma, monto]) => ({ forma, monto, parteBp: total > 0 ? Math.round((monto * 10_000) / total) : 0 }))
    .sort((a, b) => b.monto - a.monto);
}

/** When it sells: the sales counted by day of the week (Sunday first) and hour, in Nicaragua. */
export interface CalorPorHora {
  /** [day 0-6][hour 0-23]: how many sales. */
  celdas: number[][];
  max: number;
  /** The first and last hour anything sold: the hours shown. */
  desde: number;
  hasta: number;
}

const DIA_SEMANA: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const enNicaragua = new Intl.DateTimeFormat('en-US', { timeZone: ZONA_HORARIA, weekday: 'short', hour: 'numeric', hourCycle: 'h23' });

/** The moments the sales were rung, as their records' instants; null when there are none. */
export function calorPorHora(momentos: readonly string[]): CalorPorHora | null {
  if (momentos.length === 0) return null;
  const celdas = Array.from({ length: 7 }, () => Array<number>(24).fill(0));
  let desde = 23;
  let hasta = 0;
  for (const m of momentos) {
    const partes = enNicaragua.formatToParts(new Date(m));
    const dia = DIA_SEMANA[partes.find((x) => x.type === 'weekday')?.value ?? ''];
    const hora = Number(partes.find((x) => x.type === 'hour')?.value);
    if (dia === undefined || !(hora >= 0 && hora < 24)) continue;
    celdas[dia]![hora]! += 1;
    desde = Math.min(desde, hora);
    hasta = Math.max(hasta, hora);
  }
  if (desde > hasta) return null;
  return { celdas, max: Math.max(...celdas.flat()), desde, hasta };
}

/** The day of the week and the hour that rang the most sales: the first of a tie, Sunday first. */
export function horaPico(calor: CalorPorHora): { dia: number; hora: number } {
  let pico = { dia: 0, hora: calor.desde, n: -1 };
  calor.celdas.forEach((horas, dia) =>
    horas.forEach((n, hora) => {
      if (n > pico.n) pico = { dia, hora, n };
    }),
  );
  return { dia: pico.dia, hora: pico.hora };
}

// ---------------------------------------------------------------------------
// Los productos
// ---------------------------------------------------------------------------

/**
 * The rows the books name in Spanish whatever the screen's language — the quick sales outside the
 * catalogue, the counter's sales with nobody on them — named in the screen's (2026-10-13).
 */
export function filasDichas(filas: readonly FilaUtilidad[], t: (clave: 'utilidad.sinCatalogo' | 'utilidad.mostrador') => string): FilaUtilidad[] {
  return filas.map((f) =>
    f.clave === 'sin-catalogo' ? { ...f, nombre: t('utilidad.sinCatalogo') } : f.clave === 'mostrador' ? { ...f, nombre: t('utilidad.mostrador') } : f,
  );
}

/** The products that sold, quick sales outside the catalogue aside: they carry no cost. */
export const delCatalogo = (filas: readonly FilaUtilidad[]): FilaUtilidad[] => filas.filter((f) => f.clave !== 'sin-catalogo');

/**
 * How few products leave most of the profit: the fewest, best first, whose profit reaches four
 * fifths of what all of them left together, out of how many sold. Null when nothing left profit.
 */
export function concentracion(filas: readonly FilaUtilidad[]): { productos: number; deTantos: number } | null {
  const vendidos = delCatalogo(filas);
  const conUtilidad = vendidos.filter((f) => f.utilidad > 0).sort((a, b) => b.utilidad - a.utilidad);
  const total = conUtilidad.reduce((a, f) => a + f.utilidad, 0);
  if (total <= 0) return null;
  let acumulado = 0;
  let productos = 0;
  for (const f of conUtilidad) {
    acumulado += f.utilidad;
    productos += 1;
    if (acumulado * 5 >= total * 4) break;
  }
  return { productos, deTantos: vendidos.length };
}

/** What sold under the minimum margin, those at a loss first. */
export function bajoElMargen(filas: readonly FilaUtilidad[], minimoBp: number): FilaUtilidad[] {
  return delCatalogo(filas)
    .filter((f) => f.ingreso > 0 && f.margenBp !== null && f.margenBp < minimoBp)
    .sort((a, b) => (a.margenBp ?? 0) - (b.margenBp ?? 0) || b.ingreso - a.ingreso);
}

/** The last day each product sold, of the sales given. */
export function ultimaVentaPorProducto(ventas: readonly Venta[]): Map<string, string> {
  const ultima = new Map<string, string>();
  for (const v of ventas) {
    if (v.anulada) continue;
    for (const l of v.lineas) {
      if (!l.productoId) continue;
      const antes = ultima.get(l.productoId);
      if (!antes || v.fecha > antes) ultima.set(l.productoId, v.fecha);
    }
  }
  return ultima;
}

/** The last day each product came in on a purchase not voided. */
export function ultimaCompraPorProducto(compras: readonly Compra[]): Map<string, string> {
  const ultima = new Map<string, string>();
  for (const c of compras) {
    if (c.anulada) continue;
    for (const l of c.lineas) {
      const antes = ultima.get(l.productoId);
      if (!antes || c.fecha > antes) ultima.set(l.productoId, c.fecha);
    }
  }
  return ultima;
}

export interface Parado {
  productoId: string;
  nombre: string;
  cantidad: Milli;
  unidad: string;
  /** What it cost, at the average it is carried at. */
  valor: Centavos;
  /** The last day it sold; null if never, as far back as the sales given go. */
  ultimaVenta: string | null;
}

/**
 * What is sitting on the shelf: products with stock that have not sold in `dias` days, nor come in
 * on a purchase in that time (what just arrived has not had the chance), the dearest first. What
 * it is carried at is money the shop paid and cannot use.
 */
export function mercaderiaParada(d: {
  productos: readonly Producto[];
  existencias: readonly Existencia[];
  ultimaVenta: ReadonlyMap<string, string>;
  ultimaCompra: ReadonlyMap<string, string>;
  hoy: string;
  dias?: number;
}): Parado[] {
  const corte = sumarDias(d.hoy, -(d.dias ?? 30));
  const porProducto = new Map<string, { cantidad: number; valor: number }>();
  for (const e of d.existencias) {
    if (e.cantidad <= 0) continue;
    const x = porProducto.get(e.productoId) ?? { cantidad: 0, valor: 0 };
    x.cantidad += e.cantidad;
    x.valor += costOf(e.costoPromedio, e.cantidad);
    porProducto.set(e.productoId, x);
  }
  const parados: Parado[] = [];
  for (const p of d.productos) {
    const hay = porProducto.get(p.id);
    if (!p.activo || !hay) continue;
    const vendido = d.ultimaVenta.get(p.id) ?? null;
    const comprado = d.ultimaCompra.get(p.id) ?? null;
    if ((vendido && vendido > corte) || (comprado && comprado > corte)) continue;
    parados.push({ productoId: p.id, nombre: p.nombre, cantidad: hay.cantidad as Milli, unidad: p.unidadBase, valor: hay.valor as Centavos, ultimaVenta: vendido });
  }
  return parados.sort((a, b) => b.valor - a.valor || a.nombre.localeCompare(b.nombre, 'es'));
}

// ---------------------------------------------------------------------------
// Los clientes
// ---------------------------------------------------------------------------

/** The ages fiado is told apart by: up to 30 days, 31 to 60, 61 to 90, and older. */
export const TRAMOS_FIADO = ['30', '60', '90', 'mas'] as const;
export type TramoFiado = (typeof TRAMOS_FIADO)[number];

const tramoDeEdad = (dias: number): TramoFiado => (dias <= 30 ? '30' : dias <= 60 ? '60' : dias <= 90 ? '90' : 'mas');

export interface CuentaConFiado {
  clienteId: string;
  nombre: string;
  /** What of each fiado is still owed, oldest first (`pendientesDelFiado`). */
  pendientes: readonly { fecha: string; resta: number }[];
}

/**
 * Each client who owes, with what of each fiado they still owe: their notebook laid out the way
 * their own page lays it out (`estadoDeCuenta`), paid off oldest first. The rows are anybody's;
 * each goes to its own client.
 */
export function cuentasConFiado(d: {
  deudores: readonly Pick<Cliente, 'id' | 'nombre' | 'saldoInicial' | 'saldoInicialFecha'>[];
  ventas: readonly Venta[];
  abonos: readonly Abono[];
  devoluciones: readonly Devolucion[];
  incobrables: readonly Incobrable[];
}): CuentaConFiado[] {
  const deCada = <T extends { clienteId: string | null }>(filas: readonly T[]): Map<string, T[]> => {
    const m = new Map<string, T[]>();
    for (const f of filas) {
      if (!f.clienteId) continue;
      const suyas = m.get(f.clienteId);
      if (suyas) suyas.push(f);
      else m.set(f.clienteId, [f]);
    }
    return m;
  };
  const ventas = deCada(d.ventas);
  const abonos = deCada(d.abonos);
  const devoluciones = deCada(d.devoluciones);
  const incobrables = deCada(d.incobrables);
  return d.deudores.map((c) => ({
    clienteId: c.id,
    nombre: c.nombre,
    pendientes: pendientesDelFiado(
      estadoDeCuenta(ventas.get(c.id) ?? [], abonos.get(c.id) ?? [], devoluciones.get(c.id) ?? [], c, incobrables.get(c.id) ?? []),
    ),
  }));
}

export interface FiadoPorAntiguedad {
  total: Centavos;
  tramos: { tramo: TramoFiado; monto: Centavos; clientes: number }[];
  /** Who owes, the oldest debt first: what they owe and how many days the oldest of it is. */
  clientes: { clienteId: string; nombre: string; saldo: Centavos; dias: number }[];
}

/**
 * What clients owe by how old it is: each fiado's unpaid part counted at its own age, so a client
 * who paid off last month's and owes this week's is not taken for an old debt.
 */
export function fiadoPorAntiguedad(cuentas: readonly CuentaConFiado[], hoy: string): FiadoPorAntiguedad {
  const montos: Record<TramoFiado, number> = { '30': 0, '60': 0, '90': 0, mas: 0 };
  const quienes: Record<TramoFiado, Set<string>> = { '30': new Set(), '60': new Set(), '90': new Set(), mas: new Set() };
  const clientes: FiadoPorAntiguedad['clientes'] = [];
  for (const c of cuentas) {
    let saldo = 0;
    let dias = -1;
    for (const p of c.pendientes) {
      if (p.resta <= 0) continue;
      const edad = Math.max(0, diasEntre(p.fecha, hoy));
      const tramo = tramoDeEdad(edad);
      montos[tramo] += p.resta;
      quienes[tramo].add(c.clienteId);
      saldo += p.resta;
      dias = Math.max(dias, edad);
    }
    if (saldo > 0) clientes.push({ clienteId: c.clienteId, nombre: c.nombre, saldo: saldo as Centavos, dias });
  }
  return {
    total: clientes.reduce((a, c) => a + c.saldo, 0) as Centavos,
    tramos: TRAMOS_FIADO.map((tramo) => ({ tramo, monto: montos[tramo] as Centavos, clientes: quienes[tramo].size })),
    clientes: clientes.sort((a, b) => b.dias - a.dias || b.saldo - a.saldo),
  };
}

export interface ClienteQueSeFue {
  clienteId: string;
  /** The last day they bought. */
  ultima: string;
  /** Days they bought on in the three months before the last month. */
  dias: number;
  /** What they bought in those three months. */
  ingreso: Centavos;
}

/**
 * The regulars who stopped coming: clients who bought on three days or more in the three months
 * before the last thirty days, and not once in those thirty. The ones who spent the most first.
 */
export function clientesQueSeFueron(ventas: readonly Venta[], hoy: string): ClienteQueSeFue[] {
  const reciente = sumarDias(hoy, -29);
  const desde = sumarDias(reciente, -90);
  const antes = new Map<string, { dias: Set<string>; ingreso: number; ultima: string }>();
  const volvieron = new Set<string>();
  for (const v of ventas) {
    if (v.anulada || !v.clienteId || v.fecha < desde || v.fecha > hoy) continue;
    if (v.fecha >= reciente) {
      volvieron.add(v.clienteId);
      continue;
    }
    const x = antes.get(v.clienteId) ?? { dias: new Set<string>(), ingreso: 0, ultima: v.fecha };
    x.dias.add(v.fecha);
    x.ingreso += netoDeVenta(v).ingreso;
    if (v.fecha > x.ultima) x.ultima = v.fecha;
    antes.set(v.clienteId, x);
  }
  return [...antes.entries()]
    .filter(([id, x]) => x.dias.size >= 3 && !volvieron.has(id))
    .map(([clienteId, x]) => ({ clienteId, ultima: x.ultima, dias: x.dias.size, ingreso: x.ingreso as Centavos }))
    .sort((a, b) => b.ingreso - a.ingreso);
}

// ---------------------------------------------------------------------------
// Los costos
// ---------------------------------------------------------------------------

/**
 * What has to be sold a day for what is left of it to pay the expenses, at the margin the sales
 * leave: the expenses of a day over the gross margin. Null while nothing sold at a margin.
 */
export function equilibrio(d: { gastos: number; ingreso: number; utilidad: number; dias: number }): {
  necesitaPorDia: Centavos | null;
  vendePorDia: Centavos;
} {
  const dias = Math.max(1, d.dias);
  const vendePorDia = Math.round(d.ingreso / dias) as Centavos;
  if (d.ingreso <= 0 || d.utilidad <= 0) return { necesitaPorDia: null, vendePorDia };
  return { necesitaPorDia: Math.round((d.gastos / dias) * (d.ingreso / d.utilidad)) as Centavos, vendePorDia };
}

export interface AlzaDeCosto {
  productoId: string;
  proveedorId: string;
  fecha: string;
  /** What the supplier charged for one base unit, before and now, in córdobas without the freight. */
  antes: number;
  ahora: number;
  alzaBp: number;
}

/**
 * What a supplier charged for one base unit on a purchase line: the line in córdobas less its
 * share of the freight, which is the trip's and not the goods', and of the invoice's IVA, which
 * would make the first purchase entered with it look like a rise. Null for goods that came free.
 */
export function precioDeLinea(l: Compra['lineas'][number]): number | null {
  const conFlete = l.costoTotal + l.fleteAsignado + (l.ivaAsignado ?? 0);
  if (l.cantidadBase <= 0 || conFlete <= 0 || l.costoTotal <= 0) return null;
  return ((l.costoCordobas * l.costoTotal) / conFlete / l.cantidadBase) * 1_000;
}

/**
 * What came in dearer than the time before: each product bought in the stretch at a price per unit
 * at least `umbralBp` over its previous purchase, from whoever. The latest rise of each, the
 * biggest first, and by supplier how many products they raised and by how much on average.
 */
export function alzasDeCosto(
  compras: readonly Compra[],
  t: Tramo,
  umbralBp = 500,
): { alzas: AlzaDeCosto[]; porProveedor: { proveedorId: string; productos: number; promedioBp: number }[] } {
  const enOrden = compras.filter((c) => !c.anulada && c.lineas.length > 0).sort((a, b) => a.fecha.localeCompare(b.fecha) || a.id.localeCompare(b.id));
  const anterior = new Map<string, number>();
  const alzas = new Map<string, AlzaDeCosto>();
  for (const c of enOrden) {
    for (const l of c.lineas) {
      const precio = precioDeLinea(l);
      if (precio === null) continue;
      const antes = anterior.get(l.productoId);
      if (antes !== undefined && dentro(c.fecha, t)) {
        const alzaBp = Math.round(((precio - antes) * 10_000) / antes);
        const alza = alzas.get(l.productoId);
        if (alzaBp >= umbralBp) alzas.set(l.productoId, { productoId: l.productoId, proveedorId: c.proveedorId, fecha: c.fecha, antes, ahora: precio, alzaBp });
        else if (alza) {
          /*
           * A later purchase is held against the price before the rise, not the purchase before it,
           * which is the risen price itself: bought again at C$ 12 after rising from 10 to 12, the
           * rise was gone from the list (2026-10-13). Back near the old price it is taken off; still
           * over it by the threshold, it stands, at what is paid now.
           */
          const desdeAntes = Math.round(((precio - alza.antes) * 10_000) / alza.antes);
          if (desdeAntes < umbralBp) alzas.delete(l.productoId);
          else alzas.set(l.productoId, { ...alza, ahora: precio, alzaBp: desdeAntes });
        }
      }
      anterior.set(l.productoId, precio);
    }
  }
  const lista = [...alzas.values()].sort((a, b) => b.alzaBp - a.alzaBp);
  const porProveedor = new Map<string, number[]>();
  for (const a of lista) porProveedor.set(a.proveedorId, [...(porProveedor.get(a.proveedorId) ?? []), a.alzaBp]);
  return {
    alzas: lista,
    porProveedor: [...porProveedor.entries()]
      .map(([proveedorId, bps]) => ({ proveedorId, productos: bps.length, promedioBp: Math.round(bps.reduce((a, b) => a + b, 0) / bps.length) }))
      .sort((a, b) => b.productos - a.productos || b.promedioBp - a.promedioBp),
  };
}
